import * as THREE from 'three';
import { createBoat, type BoatModel } from '../assets/modelFactories/BoatFactory';
import { materialLibrary as M } from '../assets/MaterialLibrary';
import { sampleWave } from './Boat';

export type RivalConfig = {
  name: string;
  color: string;
  laneOffset: number;
  // 0..1 skill: gates corner speed and line width
  skill: number;
};

// Path-following rival: races the gate circuit with per-rival line offsets,
// speed variance, rubber-banding toward the player, and buoyancy bob.
export class RivalBoat {
  readonly group = new THREE.Group();
  readonly name: string;
  lap = 1;
  gateIndex = 0;
  private pathT = 0; // progress along current gate segment
  private targetSpeed = 18;

  constructor(
    config: RivalConfig,
    private readonly gatePath: Array<{ x: number; z: number }>,
  ) {
    this.name = config.name;
    this.model = createBoat();
    // Rival liveries: recolor the primary hull via clone-and-tint
    const hullMat = (this.model.root.children[0] as THREE.Mesh).material as THREE.MeshPhysicalMaterial;
    const tinted = hullMat.clone();
    tinted.color = new THREE.Color(config.color);
    (this.model.root.children[0] as THREE.Mesh).material = tinted;
    this.group.add(this.model.root);

    this.laneOffset = config.laneOffset;
    this.skill = config.skill;
    this.gatePath = gatePath;
    this.group.position.set(
      gatePath[0].x + config.laneOffset,
      0,
      gatePath[0].z - 6,
    );
  }

  private model: BoatModel;
  private laneOffset: number;
  private skill: number;

  update(delta: number, elapsed: number, playerProgress: number, playerSpeed: number): void {
    // Path progress: constant baseline + skill, rubber-banded to player.
    const segment = this.gateIndex % this.gatePath.length;
    const target = this.gatePath[segment];

    const dx = target.x + this.laneOffset - this.group.position.x;
    const dz = target.z - this.group.position.z;
    const dist = Math.hypot(dx, dz);
    // Continuous progress along the current segment so ranking is smooth.
    this.pathT = 1 - THREE.MathUtils.clamp(dist / 60, 0, 0.99);

    if (dist < 8) {
      this.gateIndex += 1;
      if (this.gateIndex % this.gatePath.length === 0) this.lap += 1;
      return;
    }

    // Rubber-banding: behind player -> catch up; ahead -> ease off
    const gap = playerProgress - this.progress;
    const band = THREE.MathUtils.clamp(gap * 0.15, -3.5, 4.5);
    this.targetSpeed = 17 + this.skill * 6 + band + Math.sin(elapsed * 0.7 + this.skill * 9) * 1.2;
    this.currentSpeed += (this.targetSpeed - this.currentSpeed) * Math.min(1, delta * 1.5);
    void playerSpeed;

    const nx = dx / dist;
    const nz = dz / dist;
    this.group.position.x += nx * this.currentSpeed * delta;
    this.group.position.z += nz * this.currentSpeed * delta;

    // Face travel direction
    const desiredHeading = Math.atan2(nx, nz);
    let diff = desiredHeading - this.group.rotation.y;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    this.group.rotation.y += diff * Math.min(1, delta * 4);

    // Buoyancy on the same wave field
    const wave = sampleWave(this.group.position.x, this.group.position.z, elapsed);
    this.group.position.y += (wave.y * 0.5 - this.group.position.y) * Math.min(1, delta * 6);
    this.group.rotation.x = -wave.slopeZ * 0.4;
    this.group.rotation.z = wave.slopeX * 0.3 + diff * 0.4;

    // Spin props
    const spin = delta * (12 + this.currentSpeed * 1.5);
    for (const eng of this.model.engines) {
      eng.children[3].rotation.y += spin * 8;
    }
  }

  private currentSpeed = 14;

  // Unified progress metric: laps * gates + segment t (for ranking)
  get progress(): number {
    // gateIndex accumulates across laps; lap is derived, not added — else laps
    // double-count and ranking misplaces everyone after lap 1.
    return this.gateIndex + this.pathT;
  }

  get finished(): boolean {
    return this.lap > 3;
  }

  get position(): THREE.Vector3 {
    return this.group.position;
  }
}

// Rival field config: distinct silhouettes/colors, staggered lines, varied skill
export const RIVAL_CONFIGS: RivalConfig[] = [
  { name: 'MARLIN', color: '#2dd4bf', laneOffset: -6, skill: 0.85 },
  { name: 'TEMPEST', color: '#fbbf24', laneOffset: 5, skill: 0.7 },
  { name: 'SIREN', color: '#a78bfa', laneOffset: -4, skill: 0.55 },
];

export function createRivalField(gatePath: Array<{ x: number; z: number }>): RivalBoat[] {
  return RIVAL_CONFIGS.map((config) => {
    const rival = new RivalBoat(config, gatePath);
    void M; // material library used via factory
    return rival;
  });
}
